import Chart from 'chart.js/auto';
import { getRelativePosition } from 'chart.js/helpers';

class LineChart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
    this.initializeChart();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.render();
    this.initializeChart();
  }

  render() {
    this.innerHTML = `
      <div class="mt-4 relative">
        <style>
            .custom-tooltip::after {
                border-top: 10px solid transparent;
                border-left: 12px solid black;
                border-bottom: 10px solid transparent;
            }
        </style>
        <canvas id="myChart" height="250" width="100%"></canvas>
        <div id="custom-tooltip" class="custom-tooltip pointer-events-none bg-black text-white absolute rotate-45 transform font-mono tracking-tight py-0.5 pl-1.5 pr-0.5 text-base opacity-0 after:content-[''] after:w-4 after:h-full after:absolute after:-right-2 after:left-[100%] after:top-0"></div>
      </div>`;
  }

  initializeChart() {
    const canvas = this.querySelector('#myChart');
    const ctx = canvas.getContext('2d');

    // Plugin to draw vertical line
    const verticalLinePlugin = {
      id: 'verticalLinePlugin',
      afterDraw: (chart) => {
        const currentMonth = new Date().getMonth(); // get the current month (0-11)
        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        const context = chart.ctx;

        // Get the position for the current month
        const xPosition = xScale.getPixelForValue(chart.data.labels[currentMonth]);

        // Draw the line
        context.save();
        context.beginPath();
        context.moveTo(xPosition, yScale.top);
        context.lineTo(xPosition, yScale.bottom);
        context.lineWidth = 1;
        context.strokeStyle = 'black';
        context.stroke();
        context.restore();

        // add a triangle to the bottom of the line
        context.save();
        context.beginPath();
        context.moveTo(xPosition + 5, yScale.bottom + 8);
        context.lineTo(xPosition, yScale.bottom);
        context.lineTo(xPosition - 5, yScale.bottom + 8);
        context.fillStyle = 'black';
        context.fill();

        context.restore();
      },
    };

    // Register the plugin
    Chart.register(verticalLinePlugin);

    const externalTooltipHandler = (context) => {
      const { chart, tooltip } = context;
      const tooltipEl = document.querySelector('.custom-tooltip');

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

      function formatTooltipLabel(tooltip) {
        if (!tooltip.body || !tooltip.body.length) {
          return '';
        }
        const label = tooltip.body[0].lines[0].split(':')[1].trim();
        return `${label} KWh`;
      }
      tooltipEl.innerHTML = formatTooltipLabel(tooltip);

      const tooltipWidth = tooltipEl.offsetWidth;
      const tooltipHeight = tooltipEl.offsetHeight;

      // Calculate the offset for the 45-degree rotated element
      const diagonalLength = Math.sqrt(Math.pow(tooltipWidth, 2) + Math.pow(tooltipHeight, 2));
      const offsetX = diagonalLength / 2;
      const offsetY = offsetX / 2;

      tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      tooltipEl.style.top = positionY + tooltip.caretY - offsetY + 'px';
      tooltipEl.style.opacity = 1;
      tooltipEl.style.transform = `translate(calc(-160% + ${offsetX}px), -130%) rotate(45deg)`;
      tooltipEl.style.transition = 'opacity 0.2s ease-in-out';
    };

    new Chart(ctx, {
      type: 'line',
      data: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
          {
            label: 'KWh',
            data: [38, 10, 5, 100, 20, 30, 45, 98, 46],
            borderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 1)',
            pointBorderColor: 'rgba(0, 0, 0, 1)',
            pointRadius: 2,
            fill: true,
            backgroundColor: (context) => {
              const chartArea = context.chart.chartArea;
              if (!chartArea) {
                // This case happens on initial chart load
                return null;
              }
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0.45, 'rgba(0, 0, 0, 0.17)');
              gradient.addColorStop(1, 'rgba(128, 128, 128, 0)');
              return gradient;
            },
          },
          {
            label: 'KWh',
            data: [null, null, null, null, null, null, null, null, 46, 45, 98, 46],
            borderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 1)',
            pointBorderColor: 'rgba(0, 0, 0, 1)',
            pointRadius: 2,
            fill: true,
            backgroundColor: (context) => {
              const chartArea = context.chart.chartArea;
              if (!chartArea) {
                // This case happens on initial chart load
                return null;
              }
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0.45, 'rgba(0, 0, 0, 0.8)');
              gradient.addColorStop(1, 'rgba(128, 128, 128, 0)');
              return gradient;
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            border: {
              dash: [2.5],
              dashOffset: 100,
            },

            grid: {
              color: function (context) {
                if (context.tick.value === 20) {
                  return '#41B870';
                }
                if (context.tick.value === 40) {
                  return 'rgba(0, 0, 0, 0.28)';
                }
                if (context.tick.value === 80) {
                  return '#EE6262';
                }
                return 'transparent';
              },
            },
            ticks: {
              align: 'start',
              stepSize: 20,
              padding: 10,
              font: {
                size: 14,
                weight: 500,
              },
              callback: function (value, index, values) {
                //hide the first tick label
                if (index === 0) {
                  return null;
                }
                return value + '%';
              },
            },
          },
          x: {
            grid: {
              display: true,
              drawOnChartArea: true,
              drawTicks: true,
            },
            ticks: {
              padding: 20,
              font: {
                size: 14,
                weight: 500,
              },
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            external: function (context) {
              externalTooltipHandler(context);
            },
          },
          verticalLinePlugin: {
            display: true,
          },
        },
      },
    });
  }
}

customElements.define('line-chart', LineChart);
